import React from 'react';
import { Users, ShoppingBag, CheckCircle, BarChart3, TrendingUp, AlertCircle, DollarSign, Activity } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';

const MOCK_STATS = [
  { label: 'Total Users', value: '2,450', icon: Users, color: 'text-primary', bg: 'bg-primary/5' },
  { label: 'Active Listings', value: '1,284', icon: ShoppingBag, color: 'text-primary', bg: 'bg-primary/5' },
  { label: 'Verified Vendors', value: '142', icon: CheckCircle, color: 'text-amber-600', bg: 'bg-amber-50' },
  { label: 'Total Revenue', value: '$45.2k', icon: DollarSign, color: 'text-primary', bg: 'bg-primary/10' },
];

const REVENUE_DATA = [
  { name: 'Jan', value: 4000 },
  { name: 'Feb', value: 3000 },
  { name: 'Mar', value: 5000 },
  { name: 'Apr', value: 4500 },
  { name: 'May', value: 6000 },
  { name: 'Jun', value: 5500 },
];

export const AdminDashboard = () => {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Platform Analytics</h1>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-500">Last updated: Just now</span>
          <button className="px-4 py-2 bg-primary text-white text-sm font-bold rounded-lg shadow-sm">Export Report</button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {MOCK_STATS.map((stat) => (
          <div key={stat.label} className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className={cn("p-2 rounded-lg", stat.bg)}>
                <stat.icon className={stat.color} size={24} />
              </div>
              <span className="text-xs font-bold text-primary bg-primary/5 px-2 py-1 rounded">+12.5%</span>
            </div>
            <p className="text-sm font-medium text-gray-500 mb-1">{stat.label}</p>
            <h3 className="text-2xl font-bold text-gray-900">{stat.value}</h3>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-bold text-gray-900 flex items-center gap-2">
              <BarChart3 size={20} className="text-gray-400" />
              Revenue Growth
            </h2>
            <select className="text-xs border-none bg-gray-50 rounded px-2 py-1 focus:ring-0">
              <option>Last 6 Months</option>
              <option>Last Year</option>
            </select>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={REVENUE_DATA}>
                <defs>
                  <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#CC10FE" stopOpacity={0.1}/>
                    <stop offset="95%" stopColor="#CC10FE" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#9CA3AF' }} dy={10} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#9CA3AF' }} />
                <Tooltip 
                  contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                  itemStyle={{ color: '#CC10FE', fontWeight: 600 }}
                />
                <Area type="monotone" dataKey="value" stroke="#CC10FE" strokeWidth={3} fillOpacity={1} fill="url(#colorValue)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-bold text-gray-900 flex items-center gap-2">
              <Activity size={20} className="text-gray-400" />
              Platform Activity
            </h2>
          </div>
          <div className="space-y-6">
            {[
              { label: 'Pending Vendor Approvals', count: 12, color: 'bg-amber-500' },
              { label: 'Reported Listings', count: 4, color: 'bg-red-500' },
              { label: 'Support Inquiries', count: 28, color: 'bg-red-500' }
            ].map(item => (
              <div key={item.label}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">{item.label}</span>
                  <span className="text-sm font-bold text-gray-900">{item.count}</span>
                </div>
                <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                  <div className={cn("h-full rounded-full", item.color)} style={{ width: `${(item.count / 40) * 100}%` }}></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

function cn(...inputs) {
  return inputs.filter(Boolean).join(' ');
}
